/**
 * /admin/reports — Trust & safety queue.
 *
 * Lists every user-submitted report (dog, kennel, upcoming litter,
 * user, store, event). Sorted by severity desc, then created_at desc.
 * Admin can dismiss, warn, shadow-hide, hard-hide, or ban directly
 * from a row drawer.
 *
 * Sister endpoint: POST /api/reports lets any user/anon flag content.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/utils-app";
import { FlagIcon as Flag, WarningIcon as Warning, EyeIcon as Eye, EyeSlashIcon as EyeSlash, ShieldCheckIcon as ShieldCheck } from "@phosphor-icons/react";

const REASON_LABEL = {
  fraud: "Fraud / scam",
  misrepresentation: "Misrepresentation",
  health: "Health / welfare",
  illegal: "Illegal activity",
  safety: "Safety concern",
  spam: "Spam",
  other: "Other",
};

const SEVERITY_STYLE = {
  high: "bg-crimson text-white",
  medium: "bg-gold text-ink",
  low: "bg-[#E5E5E5] text-ink",
};

const STATUS_STYLE = {
  open: "bg-[#FFF3CD] text-[#8A6A00] border-[#FFE58F]",
  reviewing: "bg-[#E0F2FE] text-[#075985] border-[#BAE6FD]",
  actioned: "bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]",
  dismissed: "bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]",
};

export default function AdminReports() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("open");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const [rRows, rStats] = await Promise.all([
        api.get("/admin/reports", { params }),
        api.get("/admin/reports/stats"),
      ]);
      setRows(rRows.data || []);
      setStats(rStats.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]);

  const act = async (id, action, extraNote) => {
    try {
      const r = await api.post(`/admin/reports/${id}/action`, {
        action,
        note: extraNote || null,
      });
      toast.success(`Action recorded: ${action}`);
      setSelected(r.data);
      setNote("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const unhide = async (id) => {
    try {
      const r = await api.post(`/admin/reports/${id}/unhide`);
      toast.success("Target unhidden — back to public surfaces.");
      setSelected(r.data);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addNote = async (id) => {
    if (!note.trim()) return;
    try {
      const r = await api.post(`/admin/reports/${id}/note`, {
        text: note.trim(),
      });
      setSelected(r.data);
      setNote("");
      toast.success("Note added.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const replyMailto = (r) => {
    const to = r.reporter_email || "";
    if (!to) {
      toast.error("No reporter email on file.");
      return;
    }
    const sub = encodeURIComponent(
      `Re: your report on ${r.target_snapshot?.name || "Armstrong K9 Registry"}`,
    );
    const body = encodeURIComponent(
      `Hi,\n\nThanks for flagging ${r.target_snapshot?.name || "this listing"} ` +
      `to Armstrong K9 Registry. Our trust & safety team reviewed your report ` +
      `(reason: ${REASON_LABEL[r.reason] || r.reason}).\n\n` +
      `[summary of action taken]\n\n` +
      `If you have additional information (photos, contract excerpts, vet ` +
      `records), please reply to this email and we'll incorporate it into our ` +
      `review.\n\n` +
      `— Armstrong K9 Registry trust & safety`,
    );
    window.open(`mailto:${to}?subject=${sub}&body=${body}`, "_blank");
  };

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <header>
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold font-bold mb-2">
            Trust & safety
          </p>
          <h1 className="font-spectral text-3xl tracking-tight">Reports queue</h1>
          <p className="text-sm text-[#6B6B6B] mt-1">
            Every user-submitted flag on a dog, kennel, upcoming litter, user,
            store, or event. Action within 1 business day.
          </p>
        </header>

        {stats && (
          <div className="grid sm:grid-cols-3 gap-3" data-testid="reports-stats">
            <StatTile
              label="Open · high severity"
              value={stats.open_high}
              accent="crimson"
              testId="reports-stat-open-high"
            />
            <StatTile
              label="Open · all"
              value={stats.open_total}
              testId="reports-stat-open-total"
            />
            <StatTile
              label="Actioned · last 7 days"
              value={stats.actioned_last_7d}
              testId="reports-stat-actioned-7d"
            />
          </div>
        )}

        <div className="flex flex-wrap gap-2" data-testid="reports-filter-bar">
          {["open", "reviewing", "actioned", "dismissed", "all"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`text-xs uppercase tracking-[0.2em] font-bold px-3 py-1.5 rounded-full border transition ${
                filter === f
                  ? "bg-ink text-white border-ink"
                  : "bg-white text-ink border-[#E5E5E5] hover:bg-[#FAFAFA]"
              }`}
              data-testid={`reports-filter-${f}`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="rounded-xl border border-[#EAEAEA] bg-white overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-[#6B6B6B] text-sm">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center text-[#6B6B6B]" data-testid="reports-empty-state">
              <ShieldCheck size={36} className="mx-auto mb-3 text-[#9B9B9B]" weight="duotone" />
              <p className="text-sm">No reports in this view. Good signal-to-noise.</p>
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="reports-table">
              <thead className="bg-[#FAFAFA] border-b border-[#EAEAEA]">
                <tr className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#6B6B6B]">
                  <th className="text-left px-4 py-3">Severity</th>
                  <th className="text-left px-4 py-3">Target</th>
                  <th className="text-left px-4 py-3">Reason</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">When</th>
                  <th className="text-right px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-[#F2F2F2] hover:bg-[#FAFAFA]"
                    data-testid={`reports-row-${r.id}`}
                  >
                    <td className="px-4 py-3">
                      <span
                        className={`text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-0.5 rounded ${SEVERITY_STYLE[r.severity]}`}
                      >
                        {r.severity}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{r.target_snapshot?.name}</div>
                      <Link
                        to={r.target_snapshot?.url || "#"}
                        target="_blank"
                        rel="noopener"
                        className="text-[11px] text-[#6B6B6B] hover:text-ink underline"
                      >
                        {r.target_type.replace("_", " ")} →
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs">{REASON_LABEL[r.reason] || r.reason}</div>
                      {r.details && (
                        <div className="text-[11px] text-[#6B6B6B] line-clamp-1 max-w-xs">
                          {r.details}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-0.5 rounded border ${STATUS_STYLE[r.status]}`}
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[11px] text-[#6B6B6B]">
                      {new Date(r.created_at).toLocaleString(undefined, {
                        dateStyle: "medium", timeStyle: "short",
                      })}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setSelected(r); setNote(""); }}
                        data-testid={`reports-open-${r.id}`}
                      >
                        Review →
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Sheet open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          {selected && (
            <>
              <SheetHeader>
                <div className="flex items-center gap-2">
                  <Flag size={16} className="text-crimson" />
                  <SheetTitle>
                    {selected.target_snapshot?.name}{" "}
                    <span className="text-[#6B6B6B] font-normal text-sm">
                      · {selected.target_type.replace("_", " ")}
                    </span>
                  </SheetTitle>
                </div>
                <SheetDescription>
                  Severity{" "}
                  <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-[0.2em] font-bold ${SEVERITY_STYLE[selected.severity]}`}>
                    {selected.severity}
                  </span>
                  {"  ·  "}Status{" "}
                  <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-[0.2em] font-bold border ${STATUS_STYLE[selected.status]}`}>
                    {selected.status}
                  </span>
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-5">
                <Block label="Reason">
                  <div className="font-medium">{REASON_LABEL[selected.reason] || selected.reason}</div>
                </Block>

                <Block label="Reporter details">
                  <div className="text-sm whitespace-pre-wrap text-[#3F3F3F]">
                    {selected.details || <em className="text-[#9B9B9B]">No details provided.</em>}
                  </div>
                </Block>

                <Block label="Reporter">
                  <div className="text-sm">
                    {selected.reporter_user_id ? (
                      <Link
                        to={`/admin/users/${selected.reporter_user_id}`}
                        className="underline"
                      >
                        {selected.reporter_email || selected.reporter_user_id}
                      </Link>
                    ) : (
                      <span>{selected.reporter_email || "Anonymous"}</span>
                    )}
                  </div>
                  <div className="text-[11px] text-[#9B9B9B] mt-1">
                    IP {selected.reporter_ip || "—"}
                  </div>
                </Block>

                <Block label="Internal notes">
                  {(selected.internal_notes || []).length === 0 ? (
                    <div className="text-[11px] text-[#9B9B9B] italic">
                      No notes yet.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {selected.internal_notes.map((n, i) => (
                        <div
                          key={i}
                          className="text-xs bg-[#FAFAFA] border border-[#F2F2F2] rounded p-2"
                        >
                          <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#9B9B9B]">
                            {n.by_email || n.by} ·{" "}
                            {new Date(n.at).toLocaleString(undefined, {
                              dateStyle: "short", timeStyle: "short",
                            })}
                          </div>
                          <div className="mt-1 whitespace-pre-wrap">{n.text}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  <Textarea
                    rows={2}
                    placeholder="Add a note (internal — reporter does not see this)…"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className="mt-2 text-sm"
                    data-testid="reports-note-input"
                  />
                  <div className="flex gap-2 justify-end mt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => addNote(selected.id)}
                      disabled={!note.trim()}
                      data-testid="reports-note-add"
                    >
                      Add note
                    </Button>
                  </div>
                </Block>

                {selected.action_taken && (
                  <div className="rounded border border-gold/40 bg-gold/5 p-3 text-xs">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#8A6A00]">
                      Action taken
                    </div>
                    <div className="mt-1">
                      <strong>{selected.action_taken.replace("_", " ")}</strong>
                      {" by "}
                      {selected.actioned_by}
                      {" at "}
                      {new Date(selected.actioned_at).toLocaleString()}
                    </div>
                    {(selected.action_taken === "shadow_hidden" ||
                      selected.action_taken === "hard_hidden") && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2"
                        onClick={() => unhide(selected.id)}
                        data-testid="reports-unhide-btn"
                      >
                        <Eye size={14} className="mr-1" /> Unhide target
                      </Button>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2 pt-3 border-t border-[#EAEAEA]">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => act(selected.id, "dismissed")}
                    data-testid="reports-dismiss-btn"
                  >
                    Dismiss
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => act(selected.id, "warned", note)}
                    data-testid="reports-warn-btn"
                  >
                    <Warning size={14} className="mr-1" weight="bold" /> Warn (log only)
                  </Button>
                  <Button
                    size="sm"
                    className="bg-gold hover:bg-gold/90 text-ink"
                    onClick={() => act(selected.id, "shadow_hidden", note)}
                    data-testid="reports-shadow-hide-btn"
                  >
                    <EyeSlash size={14} className="mr-1" /> Shadow-hide
                  </Button>
                  <Button
                    size="sm"
                    className="bg-crimson hover:bg-crimson/90 text-white"
                    onClick={() => act(selected.id, "hard_hidden", note)}
                    data-testid="reports-hard-hide-btn"
                  >
                    Hard-hide
                  </Button>
                  {selected.target_type === "user" && (
                    <Button
                      size="sm"
                      className="bg-crimson hover:bg-crimson/90 text-white col-span-2"
                      onClick={() => act(selected.id, "banned", note)}
                      data-testid="reports-ban-btn"
                    >
                      Ban user
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="col-span-2"
                    onClick={() => replyMailto(selected)}
                    disabled={!selected.reporter_email}
                    data-testid="reports-reply-btn"
                  >
                    Reply to reporter (pre-fill email)
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </AppShell>
  );
}

function StatTile({ label, value, accent, testId }) {
  return (
    <div
      className={`rounded-xl border p-4 ${
        accent === "crimson"
          ? "border-crimson/30 bg-crimson/5"
          : "border-[#EAEAEA] bg-white"
      }`}
      data-testid={testId}
    >
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#6B6B6B]">
        {label}
      </div>
      <div className="text-3xl font-spectral tracking-tight mt-1">{value}</div>
    </div>
  );
}

function Block({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#6B6B6B] mb-1">
        {label}
      </div>
      {children}
    </div>
  );
}
